// Audio output with two buses: return audio (programme sound) and talkback.
//
// Playback goes through <audio> elements wherever possible: browsers feed
// those into their echo canceller as the far-end reference, which matters
// because operators may use a phone speaker with an open mic. iOS ignores
// HTMLMediaElement.volume, so there the per-bus volume uses WebAudio gain nodes
// (iOS's voice-processing I/O cancels echo for all output anyway).

export const isIOS =
  /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

type Bus = 'return' | 'talk';

interface Output {
  bus: Bus;
  el: HTMLAudioElement;
  node?: MediaStreamAudioSourceNode;
}

export class AudioOut {
  private ctx: AudioContext | null = null;
  private gains: Partial<Record<Bus, GainNode>> = {};
  private outputs = new Map<string, Output>();
  private volumes: Record<Bus, number> = { return: 0.8, talk: 1 };
  private duckFactor = 1;
  /** Linear factor applied to the talk bus while we are talking (half-duplex ducking). */
  duckGain = Math.pow(10, -12 / 20);

  /** Must run inside a user gesture ("Tap to start") to satisfy autoplay policies. */
  unlock(): void {
    if (!this.ctx) {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx({ latencyHint: 'interactive' });
      for (const bus of ['return', 'talk'] as Bus[]) {
        const g = this.ctx.createGain();
        g.connect(this.ctx.destination);
        this.gains[bus] = g;
      }
      this.applyVolumes();
    }
    void this.ctx.resume();
    // Prime media element playback while we still hold the gesture.
    for (const o of this.outputs.values()) void o.el.play().catch(() => undefined);
  }

  setVolume(bus: Bus, v: number): void {
    this.volumes[bus] = Math.max(0, Math.min(1, v));
    this.applyVolumes();
  }

  getVolume(bus: Bus): number {
    return this.volumes[bus];
  }

  setDucked(on: boolean): void {
    this.duckFactor = on ? this.duckGain : 1;
    this.applyVolumes();
  }

  private level(bus: Bus): number {
    return this.volumes[bus] * (bus === 'talk' ? this.duckFactor : 1);
  }

  private applyVolumes(): void {
    for (const bus of ['return', 'talk'] as Bus[]) {
      const g = this.gains[bus];
      if (g && this.ctx) g.gain.setTargetAtTime(this.level(bus), this.ctx.currentTime, 0.01);
    }
    for (const o of this.outputs.values()) {
      if (!isIOS) o.el.volume = this.level(o.bus);
    }
  }

  add(id: string, bus: Bus, track: MediaStreamTrack): void {
    this.remove(id);
    const stream = new MediaStream([track]);
    const el = new Audio();
    el.autoplay = true;
    el.setAttribute('playsinline', '');
    el.srcObject = stream;
    const o: Output = { bus, el };
    if (isIOS && this.ctx && this.gains[bus]) {
      el.muted = true; // the element only keeps the stream flowing; WebAudio plays it
      o.node = this.ctx.createMediaStreamSource(stream);
      o.node.connect(this.gains[bus]!);
    } else {
      el.volume = this.level(bus);
    }
    this.outputs.set(id, o);
    void el.play().catch(() => undefined);
  }

  remove(id: string): void {
    const o = this.outputs.get(id);
    if (!o) return;
    o.node?.disconnect();
    o.el.pause();
    o.el.srcObject = null;
    this.outputs.delete(id);
  }

  clear(): void {
    for (const id of [...this.outputs.keys()]) this.remove(id);
  }
}
